import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, UsersIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { usePageMeta } from '../hooks/usePageMeta';
import { readMatchParams, resolveMatchPrefill, buildMatchHeadline, readCopyOverrides } from '../utils/matchParams';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  // Anchor and campaign links carry the visitor's context
  // (?degreeInterest=&areaStudy=&mProgram=), which turns this page into a
  // program-specific landing page: it drives the widget, the headline, and the
  // school listings so all three agree. Resolved once — the URL doesn't change
  // while the page is open.
  const [prefill] = useState(() => resolveMatchPrefill(readMatchParams()));
  const [copy] = useState(() => readCopyOverrides());
  const headline = buildMatchHeadline(prefill, categoryData?.name || 'Degree', copy.headline);
  const subhead = copy.subhead || (prefill.programLabel ? `Compare accredited online ${prefill.programLabel.toLowerCase()} programs and find the right fit for your career goals.` : `${categoryData?.description || ''} Compare top accredited programs and find the right fit for your career goals.`);
  usePageMeta(`${headline} | FindOnlineColleges`, `Compare accredited online ${(prefill.programLabel || categoryData?.name || 'degree').toLowerCase()} programs. Get matched by career goals, schedule, and budget.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    degreeInterest: prefill.degreeInterest,
    areaOfStudy: prefill.areaOfStudy || categoryData?.focusValue || '',
    specialization: prefill.specialization
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load. URL context wins, so the listings match the
  // program named in the headline rather than the whole category.
  useEffect(() => {
    const area = prefill.areaOfStudy || categoryData?.focusValue;
    if (!area) return;
    fetchResults(prefill.degreeInterest, area, prefill.specialization || generalSpec?.value || '');
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value, prefill]);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1430" data-mp="2q1dnrmme897k">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1431" data-mp="2uscb365su7kb">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-1432" data-mp="1xdrnan8gpkox">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1433" data-mp="22b9nnp8eyrlb">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1434" data-mp="1ptyuf8unqpjq">
      {/* Page Header */}
      <div className="hero-bg text-white py-12" data-id="element-1435" data-mp="1prmibcbkz9el">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1436" data-mp="1ktkwijmrsvzf">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-gray-300 mb-6" aria-label="Breadcrumb" data-id="element-1437" data-mp="1gtsj9tmfm1nq">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1438" data-mp="1bufz5gmy38ou">
              <li className="inline-flex items-center" data-id="element-1439" data-mp="1737o6g5yvf9k">
                <Link to="/" className="inline-flex items-center hover:text-white transition-colors" data-id="element-1440" data-mp="171mu0rnkdyc8">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1441" data-mp="124nzw93whre1" />
                  Home
                </Link>
              </li>
              <li data-id="element-1442" data-mp="x2yp8uklbfqh">
                <div className="flex items-center" data-id="element-1443" data-mp="t3gegrmhgl1d">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1444" data-mp="t253a6qpkb08" />
                  <span className="ml-1 md:ml-2" data-id="element-1445" data-mp="o3tr3pcxxoeu">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1446" data-mp="eg7y7cebhpij">
                <div className="flex items-center" data-id="element-1447" data-mp="ef6deg7i1onm">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1448" data-mp="iiujeughgc6r" />
                  <span className="ml-1 md:ml-2 text-white font-medium" data-id="element-1449" data-mp="ig8e71h2e8rp">
                    {prefill.programLabel || categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" data-id="element-1450" data-mp="x7h6jiccq88j">
            {headline}
          </h1>
          <p className="text-lg text-gray-300 max-w-3xl" data-id="element-1451" data-mp="125ad4chkh49c">{subhead}</p>
        </div>
      </div>

      <TrustSignals data-id="element-1452" data-mp="ni7e21dlinjw" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1453" data-mp="ngwdsbjczxxj">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1454" data-mp="sfqt33i4b4cw">
          {/* Main Content - Listings */}
          <div className="lg:w-3/4 order-2 lg:order-1" data-id="element-1455" data-mp="1g5thn9mtps6r">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1456" data-mp="1kxv3nn9smx9z">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1457" data-mp="35mp274zb47yh" />
                <p className="text-gray-600 font-medium" data-id="element-1458" data-mp="30o5hf7wg927p">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1459" data-mp="3ae76o30jgqo7">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1460" data-mp="2mo35016wv0zu" />
                <div data-id="element-1461" data-mp="2hp9py4bz71xk">
                  <p className="text-red-800 font-medium" data-id="element-1462" data-mp="2hq1kdbtu6tm9">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1463" data-mp="2vp19ekl9haiy">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1464" data-mp="b0wlzobksrjw">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1465" data-mp="5zh3o40s8z57" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1466" data-mp="q3ejqzaqw1xl" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1467" data-mp="uytyrukho3vl" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1468" data-mp="13ujfhqazsa2n">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1469" data-mp="18s349k5bg58u">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1470" data-mp="1dt8yjmemk0kh" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1471" data-mp="1ioel8nfupj9m">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1472" data-mp="3ki5ez3lwo74s">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1473" data-mp="3c9810iejgpp4">
            <div className="sticky top-24" data-id="element-1474" data-mp="3c7okqrsvuenc">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-1475" data-mp="37a4xj2e7irj9">
                <div className="bg-navy px-6 py-4" style={{
                backgroundColor: "#5B4EA8"
              }} data-id="element-1476" data-mp="338tg3k5hlcm5">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1477" data-mp="3tfw1uthe80g">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1478" data-mp="3v0ipyu0pesed">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1479" data-mp="3lzudw4uvf244">
                  {/* Degree Level */}
                  <div data-id="element-1480" data-mp="2fkjxss7hf6zr">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1481" data-mp="2fgw3hqu7qgjz">
                      <option value="" data-id="element-1482" data-mp="yjgr2g7o5jfe">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1483" data-mp="toj8ahkl7vpb">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-1484" data-mp="13g71amwslksw">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-1485" data-mp="1dcijftj2d4v9">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1486" data-mp="3j7lal1e8hi96">
                      <option value="" data-id="element-1487" data-mp="395r3uc3amopp">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1488" data-mp="2va5ge9cpkcze">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-1489" data-mp="2lcqys6f1ti5i">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-1490" data-mp="2cfgoana21x1n">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1491" data-mp="2hcgwb3qgt9ve">
                      <option value="" data-id="element-1492" data-mp="45il8wpptw76">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1493" data-mp="wu68uyxxsxnw">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-1494" data-mp="n4d0s7x070oe">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 px-4 rounded-lg text-sm tracking-wider uppercase transition-colors duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2" data-id="element-1495" data-mp="i59uyl978qfd">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1496" data-mp="1n46qfqep2gpr" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 mb-6 transition-colors duration-200" data-id="element-1497" data-mp="2147rb5uylo58">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1498" data-mp="2617y5izmuuxe">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1499" data-mp="2fv6w05sobsjj" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1500" data-mp="2ku1nrx8mcrfs">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1501" data-mp="2otaj36jb4xdg">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1502" data-mp="3mgkhs66tzmp1">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1503" data-mp="3vah35is68v3e" />
              </Link>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1504" data-mp="33lx4qlcnuiof">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1505" data-mp="2ymkfjtybgzio">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-1506" data-mp="37l4ssfl3ygzi">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-1507" data-mp="rsp5bnrctgxa">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-1508" data-mp="mvffw83yt59g">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1509" data-mp="qcbc4rvea3rp" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-1510" data-mp="14cvslhdu7ni1" />
    </main>;
}